/**
 * The name the Tax Profile page gives a document, in the documents table and beside every value a
 * document gave: the label of the document's type, or, for a document with no type, the part of
 * its stored file name before the year, or 'Document'; with the document's tax year after it where
 * the row has one ('Federal Tax Package - 2024').
 *
 * The api names a value's source document with it (api/src/taxProfile/dashboard/profileFields.ts)
 * and the page names a document's row with it (frontend/src/pages/TaxProfile/wording.ts), so the
 * two always read the same.
 */

import {
  OcrDocumentType,
  TAX_DOCUMENT_KIND_LABELS,
} from '../constants/taxProfile';

/**
 * The part of a stored file name before its last ' - ', which is where the copy in the client's
 * folder carries the name the file arrived under: 'Annual Payroll Report - 2026.xlsx' gives
 * 'Annual Payroll Report'. Null when the name has no such part.
 */
const nameFromFileName = (fileName: string | null): string | null => {
  if (!fileName) return null;
  const lastSeparator = fileName.lastIndexOf(' - ');
  return lastSeparator > 0 ? fileName.slice(0, lastSeparator) : null;
};

/**
 * The document's type label. A row with no type is named by its stored file name instead, because
 * a file the onboarding form asked for carries the question's own label there, and 'Document' says
 * nothing about which file the row is.
 */
export const taxDocumentTitle = (document: {
  docType: string | null;
  onedriveFilename: string | null;
}): string =>
  (document.docType
    ? TAX_DOCUMENT_KIND_LABELS[document.docType as OcrDocumentType]
    : null) ??
  nameFromFileName(document.onedriveFilename) ??
  'Document';

/** 'Federal Tax Package - 2024', or the title alone when there is no year. */
export const withTaxYear = (title: string, year: number | null): string =>
  year ? `${title} - ${year}` : title;

/** The title with the document's year: 'Federal Tax Package - 2024'. */
export const taxDocumentName = (document: {
  docType: string | null;
  docYear: number | null;
  onedriveFilename: string | null;
}): string => withTaxYear(taxDocumentTitle(document), document.docYear);
